import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField,
    MenuItem,
    CircularProgress,
    Snackbar,
    Alert,
    IconButton,
    Grid,
    Card,
    CardMedia,
    CardContent
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { calendarService, getImageUrl } from '../service/api';

const CalendarCard = ({ calendar, period, setMonth, setYear, setOpenDialog, setDeleteDialog }) => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const images = calendar.calendarImagesUrl || [];

    useEffect(() => {
        setCurrentImageIndex(0);
    }, [calendar]);

    const handleNext = () => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const handlePrev = () => {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
        <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)', 
            height: '100%',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' }
        }}>
            {images.length > 0 ? (
                <Box sx={{ position: 'relative' }}>
                    <CardMedia
                        component="img"
                        height="260"
                        image={getImageUrl(images[currentImageIndex]?.calendarImageUrl || images[currentImageIndex])}
                        alt={`Calendar ${period} page ${currentImageIndex + 1}`}
                        sx={{ objectFit: 'contain', bgcolor: '#f8fafc', p: 2 }}
                    />
                    
                    {images.length > 1 && (
                        <>
                            <Box sx={{ 
                                position: 'absolute', top: 12, right: 12, 
                                bgcolor: 'primary.main', color: 'white', 
                                px: 1.5, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: '0.75rem',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                            }}>
                                {currentImageIndex + 1} / {images.length}
                            </Box>
                            
                            <IconButton 
                                onClick={handlePrev}
                                sx={{ 
                                    position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 0.5
                                }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            
                            <IconButton 
                                onClick={handleNext}
                                sx={{ 
                                    position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)',
                                    bgcolor: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'white' },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 0.5
                                }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </>
                    )}
                </Box>
            ) : (
                <Box sx={{ height: 260, bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary" fontWeight={500}>No Image Found</Typography>
                </Box>
            )}
            <CardContent sx={{ pb: '16px !important', pt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Uploaded On
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {new Date(calendar.createdAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                    <Box>
                        <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => {
                                setMonth(calendar.month);
                                setYear(calendar.year);
                                setOpenDialog(true);
                            }}
                            sx={{ mr: 0.5, bgcolor: 'rgba(238, 89, 20, 0.08)' }}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, month: calendar.month, year: calendar.year })}
                            sx={{ bgcolor: 'rgba(239, 68, 68, 0.08)' }}
                        >
                            <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

const Calendar = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [calendars, setCalendars] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, month: null, year: null });

    const confirmDelete = async () => {
        setLoading(true);
        try {
            await calendarService.delete(deleteDialog.month, deleteDialog.year);
            setSnackbar({ open: true, message: 'Calendar deleted successfully', severity: 'success' });
            setDeleteDialog({ open: false, month: null, year: null });
            
            // Wait slightly for DB to reflect the change completely
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchCalendars(true);
        } catch (error) {
            console.error('Delete error:', error);
            setSnackbar({ open: true, message: error.message || 'Failed to delete calendar', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchCalendars = async (silent = false) => {
        if (!silent) setFetching(true);
        try {
            const response = await calendarService.getAll();
            if (response.success && response.data && response.data.data) {
                setCalendars(response.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch calendars', error);
            if (!silent) setSnackbar({ open: true, message: 'Failed to load calendars', severity: 'error' });
        } finally {
            if (!silent) setFetching(false);
        }
    };

    useEffect(() => {
        fetchCalendars();
    }, []);

    const handleOpen = () => setOpenDialog(true);
    const handleClose = () => {
        setOpenDialog(false);
        setFiles([]);
        setMonth(new Date().getMonth() + 1);
        setYear(new Date().getFullYear());
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newSelectedFiles = Array.from(e.target.files);
            setFiles(prevFiles => {
                const combined = [...prevFiles];
                newSelectedFiles.forEach(newFile => {
                    // Prevent duplicate exact files
                    if (!combined.some(f => f.name === newFile.name && f.size === newFile.size)) {
                        combined.push(newFile);
                    }
                });
                return combined;
            });
            // Reset input value so the exact same file can be selected again if mistakenly deleted
            e.target.value = null;
        }
    };

    const handleRemoveFile = (indexToRemove) => {
        setFiles(files.filter((_, index) => index !== indexToRemove));
    };

    const handleMoveFile = (index, direction) => {
        const newFiles = [...files];
        if (direction === 'up' && index > 0) {
            [newFiles[index - 1], newFiles[index]] = [newFiles[index], newFiles[index - 1]];
            setFiles(newFiles);
        } else if (direction === 'down' && index < newFiles.length - 1) {
            [newFiles[index + 1], newFiles[index]] = [newFiles[index], newFiles[index + 1]];
            setFiles(newFiles);
        }
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            setSnackbar({ open: true, message: 'Please select at least one calendar image', severity: 'error' });
            return;
        }

        const formData = new FormData();
        formData.append('Month', month);
        formData.append('Year', year);
        files.forEach((f) => formData.append('CalendarImages', f));

        setLoading(true);
        try {
            await calendarService.create(formData);
            setSnackbar({ open: true, message: 'Calendar uploaded successfully', severity: 'success' });
            handleClose();
            
            // Wait slightly for DB to reflect the change completely
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchCalendars(true);
        } catch (error) {
            console.error('Upload error:', error);
            setSnackbar({ open: true, message: error.message || 'Failed to upload calendar', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    Calendar
                </Typography>
                <Button 
                    variant="contained" 
                    startIcon={<CloudUploadIcon />}
                    onClick={handleOpen}
                    sx={{ 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(238, 89, 20, 0.2)' 
                    }}
                >
                    Upload Calendar
                </Button>
            </Box>
            {fetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                    <CircularProgress />
                </Box>
            ) : calendars.length === 0 ? (
                <Box sx={{ bgcolor: 'white', p: 4, borderRadius: 2, textAlign: 'center', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
                    <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        No calendars found. Please upload one.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {Object.entries(
                        calendars.reduce((acc, calendar) => {
                            const monthObj = months.find(m => m.value === calendar.month);
                            const monthName = monthObj ? monthObj.label : `Month ${calendar.month}`;
                            const key = `${monthName} ${calendar.year}`;
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(calendar);
                            return acc;
                        }, {})
                    ).map(([period, items]) => (
                        <Box key={period}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#1e293b' }}>
                                {period}
                            </Typography>
                            <Grid container spacing={3}>
                                {items.map((calendar) => (
                                    <Grid item xs={12} sm={6} md={4} key={calendar.id}>
                                        <CalendarCard 
                                            calendar={calendar} 
                                            period={period} 
                                            setMonth={setMonth}
                                            setYear={setYear}
                                            setOpenDialog={setOpenDialog}
                                            setDeleteDialog={setDeleteDialog}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Upload Dialog */}
            <Dialog 
                open={openDialog} 
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderRadius: 3, p: 1 }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1, fontWeight: 700 }}>
                    Upload Calendar Entry
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                select
                                fullWidth
                                label="Month"
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            >
                                {months.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                type="number"
                                fullWidth
                                label="Year"
                                value={year}
                                onChange={(e) => setYear(e.target.value)}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        </Box>
                        
                        <Box 
                            sx={{ 
                                border: '2px dashed',
                                borderColor: files.length > 0 ? 'primary.main' : 'divider',
                                borderRadius: 2,
                                p: 4,
                                textAlign: 'center',
                                bgcolor: files.length > 0 ? 'rgba(238, 89, 20, 0.04)' : 'transparent',
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <input
                                accept="image/*,image/jpeg,image/png,image/gif,image/webp,application/pdf,.jpg,.jpeg,.png,.gif,.webp,.pdf"
                                style={{ display: 'none' }}
                                id="calendar-file-upload"
                                type="file"
                                multiple
                                onChange={handleFileChange}
                            />
                            <label htmlFor="calendar-file-upload">
                                <Button 
                                    component="span" 
                                    variant={files.length > 0 ? "outlined" : "contained"} 
                                    color="primary"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ borderRadius: 2, mb: 2 }}
                                >
                                    {files.length > 0 ? 'Add More Files' : 'Browse Files'}
                                </Button>
                            </label>
                            {files.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
                                        {files.length} file(s) selected:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center', width: '100%', maxWidth: 300, mx: 'auto' }}>
                                        {files.map((f, index) => (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', bgcolor: 'rgba(0,0,0,0.03)', px: 1, py: 0.5, borderRadius: 1 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, textAlign: 'left' }}>
                                                    <strong style={{ opacity: 0.5, marginRight: 4 }}>{index + 1}.</strong> {f.name}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <IconButton size="small" disabled={index === 0} onClick={() => handleMoveFile(index, 'up')} sx={{ p: 0.5 }}>
                                                        <ArrowUpwardIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                    <IconButton size="small" disabled={index === files.length - 1} onClick={() => handleMoveFile(index, 'down')} sx={{ p: 0.5 }}>
                                                        <ArrowDownwardIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleRemoveFile(index)} sx={{ p: 0.5, ml: 0.5 }}>
                                                        <CloseIcon sx={{ fontSize: '1rem' }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {files.length === 0 && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Upload calendar image(s) here
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Button onClick={handleClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleUpload} 
                        variant="contained" 
                        disabled={loading || files.length === 0}
                        sx={{ 
                            borderRadius: 2, 
                            fontWeight: 600,
                            minWidth: 120,
                            boxShadow: '0 4px 12px rgba(238, 89, 20, 0.2)'
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog 
                open={deleteDialog.open} 
                onClose={() => setDeleteDialog({ open: false, month: null, year: null })}
                PaperProps={{ sx: { borderRadius: 3, p: 1, minWidth: 320 } }}
            >
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        Are you sure you want to delete this calendar entry? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                        onClick={() => setDeleteDialog({ open: false, month: null, year: null })} 
                        sx={{ color: 'text.secondary', fontWeight: 600 }}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={confirmDelete} 
                        variant="contained" 
                        color="error"
                        disabled={loading}
                        sx={{ borderRadius: 2, fontWeight: 600, boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)' }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar 
                open={snackbar.open} 
                autoHideDuration={6000} 
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity} 
                    sx={{ width: '100%', borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Calendar;
